import React, { useState } from "react";
import "../common/common.css";
import "./loginLanding.css";
import { useAuth } from "./AuthContext";
import { useNavigate } from "react-router-dom";
import { authApiEndpoint } from "../../utils/env";

// Centralized endpoint resolution (auth service)
const REGISTER_ENDPOINT = authApiEndpoint("/api/ApplicationUser/Register");

export default function SignupScreen() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [organization, setOrganization] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const org = organization.trim();
    const name = fullName.trim();
    const mail = email.trim();
    if (!org) return setError("Organization is required");
    if (!name) return setError("Full name is required");
    if (!mail) return setError("Email is required");
    if (!password) return setError("Password is required");
    if (password !== confirm) return setError("Passwords do not match");

    setSubmitting(true);
    try {
      const payload = {
        userName: mail,
        email: mail,
        password: password,
        fullName: name,
        organization: org,
        role: "User",
        phoneNumber: "",
      };

      const res = await fetch(REGISTER_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        let msg = "Unable to register. Please try again.";
        try {
          const data = await res.json();
          if (data?.errors) msg = Object.values(data.errors).join(" \n");
          if (data?.message) msg = data.message;
        } catch {
          /* ignore */
        }
        throw new Error(msg);
      }
      // Capture backend response (might include reference id)
      let details = {};
      try {
        details = await res.json();
      } catch {}

      // Auto-login after successful registration
      await login(mail, password, true);

      // Navigate to confirmation
      navigate("/signup/success", {
        replace: true,
        state: {
          organization: org,
          fullName: name,
          email: mail,
          reference: details?.reference || "",
          loggedIn: true,
        },
      });
    } catch (err) {
      setError(err.message || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="landing-root">
      <div className="landing-hero">
        <div className="hero-top">
          <div className="brand">
            <img
              src="/brand-logo.svg"
              alt="DIXI Vault"
              className="brand-logo"
            />
            <span className="brand-name">DIXI Vault</span>
          </div>
          <a className="link subtle" href="/" rel="noreferrer">
            Back to sign in
          </a>
        </div>

        <div className="hero-content">
          <div className="hero-copy">
            <div className="eyebrow">Start your free trial</div>
            <h1>Create your organization</h1>
            <p>
              Set your organization name and your admin user details to get
              started. You can invite teammates later from the app settings.
            </p>
          </div>
          <div className="hero-art">
            <img src="/illustration-hero.svg" alt="Get started" />
          </div>
        </div>
      </div>

      <aside className="auth-panel" aria-label="Create account">
        <form onSubmit={onSubmit} className="auth-card">
          <div className="chip">Free trial</div>
          <label className="f-label">
            Organization
            <input
              className="f-input"
              type="text"
              value={organization}
              onChange={(e) => setOrganization(e.target.value)}
              placeholder="Acme Inc."
            />
          </label>
          <label className="f-label">
            Full name
            <input
              className="f-input"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Jane Doe"
            />
          </label>
          <label className="f-label">
            Email
            <input
              className="f-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="username"
            />
          </label>
          <label className="f-label">
            Password
            <input
              className="f-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="new-password"
            />
          </label>
          <label className="f-label">
            Confirm password
            <input
              className="f-input"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="••••••••"
              autoComplete="new-password"
            />
          </label>
          {error && (
            <div className="error-text" role="alert">
              {error}
            </div>
          )}
          <button className="cta submit" disabled={submitting}>
            {submitting ? "Creating…" : "Create account"}
          </button>
          <div className="small-links">
            <a href="/">Already have an account? Sign in</a>
          </div>
        </form>

        <div className="quote" aria-label="Tip">
          <img src="/avatar-quote.svg" alt="" className="avatar" />
          <blockquote>
            You'll be signed in automatically after creating your account.
          </blockquote>
          <div className="cite">Onboarding</div>
        </div>
      </aside>
    </div>
  );
}
